import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/live-support`;

// Axios instance that always sends the player cookie/JWT
function authConfig() {
  const tk = localStorage.getItem("access_token") || localStorage.getItem("auth_token");
  return tk ? { withCredentials: true, headers: { Authorization: `Bearer ${tk}` } } : { withCredentials: true };
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return ""; }
}

// Resolve a stored avatar/file path to a full URL (handles /api/... paths)
function fileUrl(path) {
  if (!path) return "";
  if (/^https?:/i.test(path)) return path;
  return `${BACKEND_URL}${path}`;
}

// ── Safe, lightweight rich-text renderer for bot/agent messages ──
// Supports:
//   **bold**     →  <strong>
//   `code`       →  <code>
//   [text](url)  →  <a>
//   plain URLs   →  <a>
//   bullet lines starting with "• ", "- ", or "* "  → styled bullet rows
//   horizontal-rule lines made of ━ / ─ / – / =     → divider
// Renders as React nodes (no dangerouslySetInnerHTML — XSS-safe).
function RichText({ text }) {
  if (!text) return null;
  const lines = String(text).split("\n");

  const renderInline = (line, keyPrefix) => {
    // Tokenise: split by **bold**, `code`, [label](url), and bare URLs.
    const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s)]+)/g;
    const parts = [];
    let last = 0;
    let m;
    let idx = 0;
    while ((m = pattern.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      const tok = m[0];
      const k = `${keyPrefix}-${idx++}`;
      if (tok.startsWith("**")) {
        parts.push(<strong key={k} className="font-bold text-white">{tok.slice(2, -2)}</strong>);
      } else if (tok.startsWith("`")) {
        parts.push(<code key={k} className="px-1 py-0.5 rounded bg-black/30 text-emerald-200 text-[12px] font-mono">{tok.slice(1, -1)}</code>);
      } else if (tok.startsWith("[")) {
        const mm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
        if (mm) parts.push(<a key={k} href={mm[2]} target="_blank" rel="noreferrer" className="text-emerald-300 underline underline-offset-2 hover:text-emerald-200 break-all">{mm[1]}</a>);
      } else {
        parts.push(<a key={k} href={tok} target="_blank" rel="noreferrer" className="text-emerald-300 underline underline-offset-2 hover:text-emerald-200 break-all">{tok}</a>);
      }
      last = m.index + tok.length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return parts;
  };

  return (
    <>
      {lines.map((raw, i) => {
        const line = raw;
        // Horizontal-rule line: a line made mostly of separator glyphs.
        if (/^[\s━─–—=_*·]{4,}$/.test(line.trim())) {
          return <div key={i} className="my-1.5 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />;
        }
        // Bullet line (• / - / *)
        const bul = /^(\s*)([•\-*])\s+(.*)$/.exec(line);
        if (bul) {
          return (
            <div key={i} className="flex gap-1.5 leading-[1.6]">
              <span className="text-emerald-400 mt-[1px] shrink-0 select-none">•</span>
              <span className="flex-1 min-w-0">{renderInline(bul[3], `i${i}`)}</span>
            </div>
          );
        }
        // Empty line → vertical gap
        if (line.trim() === "") return <div key={i} className="h-1.5" aria-hidden />;
        return <div key={i} className="leading-[1.6]">{renderInline(line, `i${i}`)}</div>;
      })}
    </>
  );
}

export default function LiveSupportWidget() {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [tree, setTree] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [activeForm, setActiveForm] = useState(null); // {key, definition}
  const [ratingState, setRatingState] = useState(null); // {can_rate, rating, session_status}
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("ls_user_sound") !== "0");
  const [now, setNow] = useState(Date.now());
  const [topicHistory, setTopicHistory] = useState([]); // stack of topic_ids the user has drilled into
  const [pendingFiles, setPendingFiles] = useState([]); // [{file, previewUrl, uploading, url, name, mime}]
  const [imagePreview, setImagePreview] = useState(null); // lightbox: full-size URL
  const [showTickets, setShowTickets] = useState(false); // "My Tickets" panel
  const [myTickets, setMyTickets] = useState({ items: [], counts: {} });
  const [localUserTypingAt, setLocalUserTypingAt] = useState(0);
  const [confirmCloseAt, setConfirmCloseAt] = useState(0); // timestamp when user clicked "End chat" once
  const [feedbackBusy, setFeedbackBusy] = useState({}); // {[message_id]: true} during the API round-trip
  const threadRef = useRef(null);
  const lastTsRef = useRef("");
  const audioCtxRef = useRef(null);
  const seenMsgIdsRef = useRef(new Set());
  const typingPingAtRef = useRef(0);
  const bootedRef = useRef(false);
  const fileInputRef = useRef(null);
  // Synchronous lock for sendText() — React state updates are async, so two
  // rapid clicks (or Enter-press + button-click) could BOTH see sending=false
  // and fire two POSTs. A ref flips instantly and prevents that race.
  const sendInFlightRef = useRef(false);

  // Tick every 1s to refresh typing freshness AND drive the live countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Lightweight chime via Web Audio (no asset file needed)
  const playChime = useCallback(() => {
    if (!soundOn) return;
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      const ctx = audioCtxRef.current || new Ctor();
      audioCtxRef.current = ctx;
      // Two-tone — high then mid (warm, professional)
      const freqs = [988, 740];
      let t0 = ctx.currentTime;
      freqs.forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.16, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
        o.connect(g).connect(ctx.destination);
        o.start(t0);
        o.stop(t0 + 0.32);
        if (i === 0) t0 += 0.14;
      });
    } catch (_) { /* ignore */ }
  }, [soundOn]);

  const toggleSound = useCallback(() => {
    setSoundOn((v) => {
      const nv = !v;
      localStorage.setItem("ls_user_sound", nv ? "1" : "0");
      return nv;
    });
  }, []);

  // Load topic tree once
  useEffect(() => {
    axios.get(`${API}/topics-tree`).then((r) => setTree(r.data?.tree || [])).catch(() => {});
  }, []);

  // Load my-tickets when the user opens that panel (also auto-poll while open)
  useEffect(() => {
    if (!open || !user || !showTickets) return;
    let cancelled = false;
    const load = () => {
      axios.get(`${API}/my-tickets`, authConfig())
        .then((r) => { if (!cancelled) setMyTickets({ items: r.data?.items || [], counts: r.data?.counts || {} }); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [open, user, showTickets]);

  // Look up resume session when widget opens & user logged in
  useEffect(() => {
    if (!open || !user) return;
    axios.get(`${API}/my-session`, authConfig()).then(({ data }) => {
      if (data?.session) {
        setSessionId(data.session.id);
        setSession(data.session);
      }
    }).catch(() => {});
  }, [open, user]);

  // Whenever the active session transitions to "closed" — check if user can rate
  useEffect(() => {
    if (!sessionId || !session || session.status !== "closed") {
      setRatingState(null);
      return;
    }
    axios.get(`${API}/session/${sessionId}/rating-status`, authConfig())
      .then(({ data }) => setRatingState(data))
      .catch(() => {});
  }, [sessionId, session]);

  const scrollBottom = useCallback(() => {
    setTimeout(() => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, 60);
  }, []);

  // Poll messages
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const { data } = await axios.get(`${API}/session/${sessionId}/messages`, authConfig());
        if (cancelled) return;
        setSession(data?.session || null);
        const items = data?.items || [];
        setMessages(items);
        // record IDs without chiming on the initial load
        items.forEach((m) => seenMsgIdsRef.current.add(m.id));
        bootedRef.current = true;
        if (items.length) lastTsRef.current = items[items.length - 1].created_at;
        scrollBottom();
      } catch (e) {
        if (e?.response?.status === 404) { setSessionId(null); setSession(null); setMessages([]); }
      }
    };
    fetchAll();
    const t = setInterval(async () => {
      try {
        const since = lastTsRef.current ? `?since_iso=${encodeURIComponent(lastTsRef.current)}` : "";
        const { data } = await axios.get(`${API}/session/${sessionId}/messages${since}`, authConfig());
        if (cancelled) return;
        if (data?.session) setSession(data.session);
        const items = data?.items || [];
        if (items.length) {
          // chime ONLY when a brand-new agent/system message arrives that we haven't seen
          const newIncoming = items.some(
            (m) => (m.sender_type === "agent" || m.sender_type === "system") && !seenMsgIdsRef.current.has(m.id)
          );
          if (newIncoming && bootedRef.current) playChime();
          setMessages((prev) => {
            // ── Deduplicate by message id ──
            // Reasons a poll-result might re-include an already-shown message:
            //   1. We optimistically appended it on send() — server now echoes it back
            //   2. `since_iso` is inclusive on the server boundary
            //   3. Bot auto-reply arrives both in send() response AND poll result
            const seen = new Set(prev.map((m) => m.id));
            const fresh = items.filter((m) => !seen.has(m.id));
            if (fresh.length === 0) {
              // still advance lastTs so we don't keep refetching the same window
              lastTsRef.current = items[items.length - 1].created_at;
              return prev;
            }
            fresh.forEach((m) => seenMsgIdsRef.current.add(m.id));
            lastTsRef.current = items[items.length - 1].created_at;
            return [...prev, ...fresh];
          });
          scrollBottom();
        }
      } catch (_) { /* ignore */ }
    }, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, [open, sessionId, scrollBottom, playChime]);

  // Reset seen-IDs and boot flag when session changes
  useEffect(() => {
    seenMsgIdsRef.current = new Set();
    bootedRef.current = false;
  }, [sessionId]);

  // Send typing ping (throttled to every 2.5s)
  const pingTyping = useCallback(() => {
    if (!sessionId) return;
    const t = Date.now();
    setLocalUserTypingAt(t);
    if (t - typingPingAtRef.current < 2500) return;
    typingPingAtRef.current = t;
    axios.post(`${API}/session/${sessionId}/typing`, {}, authConfig()).catch(() => {});
  }, [sessionId]);

  // Is the agent currently typing?
  const agentIsTyping = (() => {
    if (!session?.agent_typing_at) return false;
    try { return (now - new Date(session.agent_typing_at).getTime()) < 6000; } catch { return false; }
  })();

  // Local user-typing freshness (recent ping < 6s ago)
  const userIsTypingLocal = localUserTypingAt > 0 && (now - localUserTypingAt) < 6000;
  const bothTyping = agentIsTyping && userIsTypingLocal;

  const startSession = async (topic) => {
    if (!user) return;
    try {
      const { data } = await axios.post(`${API}/session/start`, {
        topic_id: topic?.id || null,
        topic_label: topic?.label || null,
      }, authConfig());
      if (data?.session) {
        setSessionId(data.session.id);
        setSession(data.session);
        setMessages([]);
        lastTsRef.current = "";
        if (topic?.id) {
          // Auto-click the chosen topic so we get the bot reply / children
          await topicClick(data.session.id, topic.id);
        }
      }
    } catch (e) {
      console.warn("[ls] start failed", e?.response?.data || e);
    }
  };

  const topicClick = async (sid, topic_id, _opts = {}) => {
    try {
      await axios.post(`${API}/session/${sid}/topic-click`, { topic_id }, authConfig());
      if (!_opts.skipPush) {
        setTopicHistory((h) => [...h, topic_id]);
      }
    } catch (e) {
      console.warn("topic-click failed", e?.response?.data || e);
    }
  };

  // Bot 👍 / 👎 feedback on an Auto-Assist reply. We optimistically update
  // the local message so the UI feels instant, then POST to the backend
  // which stores both the per-message rating and the per-KB-entry
  // aggregate for the admin dashboard.
  const sendBotFeedback = async (msgId, rating) => {
    if (!sessionId || !msgId || !rating) return;
    if (feedbackBusy[msgId]) return;
    setFeedbackBusy(prev => ({ ...prev, [msgId]: true }));
    // Optimistic update — frozen value, will be overwritten if API fails.
    setMessages(prev => prev.map(mm => (
      mm.id === msgId
        ? { ...mm, feedback: { ...(mm.feedback || {}), rating, at: new Date().toISOString() } }
        : mm
    )));
    try {
      await axios.post(
        `${API}/session/${sessionId}/message/${msgId}/feedback`,
        { rating },
        authConfig(),
      );
    } catch (e) {
      console.warn("bot feedback failed", e?.response?.data || e);
      // Roll back on failure.
      setMessages(prev => prev.map(mm => (
        mm.id === msgId ? { ...mm, feedback: null } : mm
      )));
    } finally {
      setFeedbackBusy(prev => {
        const n = { ...prev }; delete n[msgId]; return n;
      });
    }
  };

  const onOptionClick = async (opt) => {
    if (opt.action === "restart_menu") {
      // Special quick-action: append a fresh root-menu bubble so the user
      // can start a new conversation without losing the chat history.
      restartMenu();
      return;
    }    if (opt.action === "resolved") {
      // Bot Auto-Assist "✅ সমাধান হয়েছে — ধন্যবাদ" button.
      // Close the chat session directly (no extra user message). The
      // close endpoint pushes its own polished farewell bubble and marks
      // the session as closed so the bot won't reply anymore.
      if (!sessionId) return;
      try {
        await axios.post(`${API}/session/${sessionId}/close`, {}, authConfig());
      } catch (e) {
        console.warn("session close failed", e?.response?.data || e);
      }
      return;
    }
    if (opt.action === "call_agent") {
      // Escalate: find the "Live Support Agent" root topic and trigger it.
      // This sets needs_agent=true on the session + notifies Telegram, and
      // emits the comprehensive professional confirmation bubble to the user.
      if (!sessionId) return;
      // Always re-fetch tree to ensure the agent topic exists in cache
      let pool = tree;
      if (!pool || pool.length === 0) {
        try {
          const r = await axios.get(`${API}/topics-tree`);
          pool = r.data?.tree || [];
          if (pool.length) setTree(pool);
        } catch { /* fall through */ }
      }
      const agentTopic = (pool || []).find(
        (t) => (t.label || "").includes("সরাসরি সাপোর্ট") || (t.label || "").startsWith("👨‍💼")
      );
      if (agentTopic?.id) {
        await topicClick(sessionId, agentTopic.id);
      }
      return;
    }
    if (typeof opt.action === "string" && opt.action.startsWith("seed:")) {
      // Bot Auto-Assist quick-topic button. Treat the click as if the user
      // typed the seed phrase — the backend will route it through the KB
      // matcher and reply with the matched topic answer (deposit/withdraw/
      // bonus/agent/refer/affiliate/etc.).
      if (!sessionId) return;
      const seedText = opt.action.slice("seed:".length).trim();
      if (!seedText) return;
      try {
        await axios.post(`${API}/session/${sessionId}/message`,
          { text: seedText }, authConfig());
      } catch (e) {
        console.warn("seed topic send failed", e?.response?.data || e);
      }
      return;
    }
    if (opt.form_key) {
      // Open the form sheet
      try {
        const { data } = await axios.get(`${API}/forms/${opt.form_key}`, authConfig());
        setActiveForm(data?.form || null);
      } catch (e) {
        console.warn("form fetch failed", e?.response?.data || e);
      }
      return;
    }
    if (!sessionId || !opt.id) return;
    await topicClick(sessionId, opt.id);
  };

  // ─── Back navigation: pop current topic, re-trigger parent (or restart) ───
  const showRootMenuInline = useCallback(async () => {
    // Re-fetch latest tree so newly-added admin topics show up without a page reload
    let latest = tree;
    try {
      const r = await axios.get(`${API}/topics-tree`);
      if (Array.isArray(r.data?.tree) && r.data.tree.length > 0) {
        latest = r.data.tree;
        setTree(latest);
      }
    } catch { /* network blip — fall back to cached tree */ }
    if (!latest || latest.length === 0) return;
    const synthetic = {
      id: `_local_menu_${Date.now()}`,
      sender_type: "system",
      sender_name: "Support",
      text: "মূল মেনু — আপনার প্রশ্ন বা সমস্যা বেছে নিন:",
      options: latest.map((t) => ({ id: t.id, label: t.label })),
      created_at: new Date().toISOString(),
      _local: true,
    };
    seenMsgIdsRef.current.add(synthetic.id);
    setMessages((m) => [...m, synthetic]);
    scrollBottom();
  }, [tree, scrollBottom]);

  const goBackOneStep = async () => {
    if (!sessionId || topicHistory.length === 0) return;
    const next = topicHistory.slice(0, -1);
    setTopicHistory(next);
    if (next.length === 0) {
      // back to root: show the topic menu inline so user always has options
      showRootMenuInline();
      return;
    }
    const parentId = next[next.length - 1];
    // re-emit parent's options without pushing to history
    await topicClick(sessionId, parentId, { skipPush: true });
    setTopicHistory(next); // keep parent on top
  };

  const restartMenu = () => {
    if (!sessionId) return;
    // Reset stack and inject a local system bubble with ALL root topics as
    // tappable options. (Previously this opened only tree[0] which felt broken.)
    setTopicHistory([]);
    showRootMenuInline();
  };

  const sendText = async () => {
    // Synchronous double-click / Enter-spam guard. Must flip BEFORE any
    // await so a second invocation in the same tick gets rejected.
    if (sendInFlightRef.current) return;
    if (!sessionId || sending) return;
    const text = input.trim();
    // Require either text or at least one *uploaded* attachment
    const uploaded = pendingFiles.filter((p) => p.url);
    if (!text && uploaded.length === 0) return;
    sendInFlightRef.current = true;
    setSending(true);
    setInput("");
    typingPingAtRef.current = 0;
    const attachments = uploaded.map((p) => ({ name: p.name, url: p.url, mime: p.mime }));
    try {
      const { data } = await axios.post(`${API}/session/${sessionId}/message`, { text, attachments }, authConfig());
      if (data?.message) {
        // Add to state ONLY if not already present (defensive — polling
        // could in theory have raced and fetched it already).
        seenMsgIdsRef.current.add(data.message.id);
        setMessages((m) => (m.some((x) => x.id === data.message.id) ? m : [...m, data.message]));
        lastTsRef.current = data.message.created_at;
        // clear pending uploads & revoke object URLs
        pendingFiles.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
        setPendingFiles([]);
        scrollBottom();
      }
    } catch (e) { setInput(text); }
    finally { sendInFlightRef.current = false; setSending(false); }
  };

  // ─── File upload: pick → upload → keep in pendingFiles, sendText attaches them ───
  const onPickFiles = async (fileList) => {
    if (!sessionId || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, 4); // up to 4 at a time
    const draftRows = files.map((f) => ({
      key: `${Date.now()}-${f.name}-${Math.random()}`,
      file: f,
      previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      uploading: true,
      url: null,
      name: f.name,
      mime: f.type,
    }));
    setPendingFiles((arr) => [...arr, ...draftRows]);
    // upload sequentially to keep server load low
    for (const row of draftRows) {
      try {
        const fd = new FormData();
        fd.append("file", row.file);
        const { data } = await axios.post(`${API}/session/${sessionId}/upload`, fd, {
          ...authConfig(),
          headers: { ...(authConfig().headers || {}), "Content-Type": "multipart/form-data" },
        });
        setPendingFiles((arr) => arr.map((r) => (r.key === row.key ? { ...r, uploading: false, url: data?.url, name: data?.name || r.name, mime: data?.mime || r.mime } : r)));
      } catch (e) {
        // mark as failed (remove it after a beat)
        setPendingFiles((arr) => arr.map((r) => (r.key === row.key ? { ...r, uploading: false, url: null, error: true } : r)));
      }
    }
  };

  const removePending = (key) => {
    setPendingFiles((arr) => {
      const row = arr.find((r) => r.key === key);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return arr.filter((r) => r.key !== key);
    });
  };

  const submitForm = async (formKey, payload) => {
    if (!sessionId) return;
    const fd = new FormData();
    fd.append("form_key", formKey);
    Object.entries(payload.fields || {}).forEach(([k, v]) => fd.append(k, v));
    Object.entries(payload.files || {}).forEach(([k, file]) => {
      if (file) fd.append(k, file);
    });
    const { data } = await axios.post(`${API}/session/${sessionId}/form-submit`, fd, {
      ...authConfig(),
      headers: { ...(authConfig().headers || {}), "Content-Type": "multipart/form-data" },
    });
    if (data?.message) {
      // Refresh next poll picks up everything; close form sheet
      setActiveForm(null);
    }
  };

  const submitRating = async (score, comment) => {
    if (!sessionId) return;
    try {
      const { data } = await axios.post(
        `${API}/session/${sessionId}/rate`,
        { score, comment },
        authConfig()
      );
      setRatingState({ can_rate: false, rating: data?.rating, session_status: "closed" });
    } catch (e) {
      throw e;
    }
  };

  // User-initiated close: lets the player end their own chat (just like agents
  // can close from their dashboard). Two-stage click (inline confirmation)
  // avoids needing window.confirm — which can be silently blocked or auto-
  // dismissed inside some embedded webviews / mobile browsers.
  const closeOwnSession = async () => {
    if (!sessionId) return;
    if (session?.status === "closed") return;
    // First click → arm the confirmation (button label flips to "নিশ্চিত?")
    if (!confirmCloseAt || Date.now() - confirmCloseAt > 5000) {
      setConfirmCloseAt(Date.now());
      // Auto-cancel the confirmation after 5 seconds if user doesn't follow through
      setTimeout(() => setConfirmCloseAt((t) => (Date.now() - t >= 5000 ? 0 : t)), 5100);
      return;
    }
    // Second click within 5s → actually close
    setConfirmCloseAt(0);
    // Optimistic UI: flip status immediately so the user gets instant feedback
    // (the button disappears + header status updates) instead of waiting up
    // to 3 seconds for the next message poll to reconcile.
    const prevSession = session;
    setSession((s) => (s ? { ...s, status: "closed", needs_agent: false } : s));
    try {
      await axios.post(`${API}/session/${sessionId}/close`, {}, authConfig());
      // Force-refresh messages right away so the closing system bubble shows
      // up without waiting for the 3-second poll cycle.
      try {
        const { data } = await axios.get(`${API}/session/${sessionId}/messages`, authConfig());
        if (data?.session) setSession(data.session);
        if (data?.items) {
          setMessages(data.items);
          if (data.items.length) lastTsRef.current = data.items[data.items.length - 1].created_at;
        }
        scrollBottom();
      } catch (_) { /* poll will reconcile */ }
    } catch (e) {
      // Roll back optimistic update on failure
      setSession(prevSession);
      const msg = e?.response?.data?.detail || "চ্যাট বন্ধ করতে পারিনি — আবার চেষ্টা করুন।";
      // Use alert as a last-resort signal — at least the user sees something
      // went wrong (vs the previous silent no-op).
      try { alert(msg); } catch { /* ignore */ }
    }
  };

  // ─────────────── Render ───────────────
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="live-support-launcher"
        aria-label="Live Support"
        className="fixed z-[9990] bottom-[84px] right-3 sm:bottom-5 sm:right-5 h-14 w-14 rounded-full shadow-[0_10px_30px_-4px_rgba(0,0,0,0.5)] flex items-center justify-center transition-transform active:scale-95 hover:scale-105"
        style={{ background: "radial-gradient(120% 120% at 30% 20%, #1cc88a 0%, #18a474 45%, #0d6b4d 100%)" }}
      >
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
          <path d="M21 14a3 3 0 0 1-3 3h-1v-6h1a3 3 0 0 1 3 3z" />
          <path d="M3 14a3 3 0 0 0 3 3h1v-6H6a3 3 0 0 0-3 3z" />
          <path d="M21 16v1a4 4 0 0 1-4 4h-3" />
        </svg>
        <span className="absolute inset-0 rounded-full animate-ping bg-emerald-400/30" />
      </button>

      {/* ──────────────── Image lightbox ──────────────── */}
      {imagePreview && (
        <div onClick={() => setImagePreview(null)} data-testid="ls-image-lightbox"
             className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out">
          <img src={imagePreview} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl" />
          <button onClick={(e) => { e.stopPropagation(); setImagePreview(null); }}
                  className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      )}

      {open && (
        <div
          data-testid="live-support-panel"
          className="fixed z-[9991] inset-0 sm:inset-auto sm:bottom-20 sm:right-5 sm:w-[400px] sm:max-w-[440px] h-[100dvh] sm:h-[600px] sm:max-h-[680px] rounded-none sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          style={{ background: "#0a0f1c", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {/* ──────────── Header (ultra-pro) ──────────── */}
          <div className="relative px-4 py-3.5 flex items-center justify-between gap-2 text-white shrink-0 overflow-hidden"
               style={{
                 background:
                   "linear-gradient(135deg, #0d9488 0%, #059669 35%, #047857 70%, #064e3b 100%)",
                 boxShadow:
                   "inset 0 -1px 0 rgba(255,255,255,0.08)," +
                   "inset 0 1px 0 rgba(255,255,255,0.10)",
               }}>
            {/* Mesh dot pattern overlay */}
            <span aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.10]"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 1px 1px, rgba(255,255,255,1) 1px, transparent 1.5px)",
                    backgroundSize: "18px 18px",
                  }} />
            {/* Soft top-right halo */}
            <span aria-hidden className="pointer-events-none absolute -top-12 -right-8 h-36 w-36 rounded-full opacity-40"
                  style={{ background: "radial-gradient(circle, #6ee7b7 0%, transparent 70%)", filter: "blur(20px)" }} />
            {/* Aurora bottom sweep */}
            <span aria-hidden className="pointer-events-none absolute -bottom-16 -left-10 h-32 w-64 opacity-30"
                  style={{ background: "radial-gradient(ellipse, #34d399 0%, transparent 65%)", filter: "blur(24px)" }} />

            <div className="flex items-center gap-3 min-w-0 relative z-10">
              {/* ── Premium brand avatar (headset + ring) ── */}
              <div className="relative shrink-0">
                {/* Live pulse rings — only when agent is actively typing */}
                {agentIsTyping && (
                  <>
                    <span aria-hidden className="absolute inset-[-2px] rounded-2xl bg-emerald-300/35 pointer-events-none"
                          style={{ animation: "ls-bot-pulse 1.4s cubic-bezier(0,0,0.2,1) infinite" }} />
                    <span aria-hidden className="absolute inset-[-2px] rounded-2xl bg-emerald-300/25 pointer-events-none"
                          style={{ animation: "ls-bot-pulse 1.4s cubic-bezier(0,0,0.2,1) 0.7s infinite" }} />
                  </>
                )}
                {/* Glow halo */}
                <span aria-hidden className="absolute inset-[-3px] rounded-2xl opacity-70"
                      style={{
                        background:
                          "conic-gradient(from 140deg, rgba(255,255,255,0.40), transparent 25%, rgba(110,231,183,0.55) 60%, transparent 85%)",
                        filter: "blur(4px)",
                      }} />
                {/* Inner disc */}
                <div className="relative h-10 w-10 rounded-2xl flex items-center justify-center ring-1 ring-white/25 overflow-hidden"
                     style={{
                       background:
                         "linear-gradient(160deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.04) 60%, rgba(0,0,0,0.18) 100%)",
                       backdropFilter: "blur(8px)",
                       boxShadow:
                         "inset 0 1px 0 rgba(255,255,255,0.30)," +
                         "inset 0 -6px 12px rgba(0,0,0,0.18)," +
                         "0 6px 18px -4px rgba(0,0,0,0.45)",
                     }}>
                  {session?.status === "claimed" && session?.agent_avatar ? (
                    <img
                      src={fileUrl(session.agent_avatar)}
                      alt={session.agent_name || "Agent"}
                      className="absolute inset-0 h-full w-full object-cover"
                      data-testid="ls-header-agent-avatar"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <>
                      {/* Top shine */}
                      <span aria-hidden className="absolute top-0.5 left-1 h-3 w-3 rounded-full opacity-60 pointer-events-none"
                            style={{ background: "radial-gradient(circle, rgba(255,255,255,0.7) 0%, transparent 70%)" }} />
                      {/* Headset glyph (Quick Message / brand) */}
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="relative drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]">
                        <path d="M3 14v-3a9 9 0 0 1 18 0v3" />
                        <path d="M21 19a2 2 0 0 1-2 2h-1v-7h3v5z" />
                        <path d="M3 19a2 2 0 0 0 2 2h1v-7H3v5z" />
                      </svg>
                    </>
                  )}
                </div>
                {/* Status dot with double ring + ping (high-contrast white ring) */}
                <span className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ring-[2.5px] ring-emerald-950 shadow-[0_0_8px_rgba(52,211,153,0.6)] ${session?.status === "claimed" ? "bg-emerald-300" : session?.status === "open" ? "bg-amber-300" : "bg-emerald-300"}`}>
                  <span className={`absolute inset-0 rounded-full ${session?.status === "claimed" ? "bg-emerald-300" : session?.status === "open" ? "bg-amber-300" : "bg-emerald-300"} opacity-70`}
                        style={{ animation: "ls-ping 1.6s cubic-bezier(0,0,0.2,1) infinite" }} />
                </span>
              </div>

              {/* ── Title + sub ── */}
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="font-black text-[15px] tracking-tight truncate"
                       style={{ textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}
                       data-testid="ls-header-title">
                    {session?.status === "claimed" && session?.agent_name
                      ? session.agent_name
                      : "Maxlive88 Support"}
                  </div>
                  {session?.status === "claimed" && session?.agent_designation && (
                    <span className="inline-flex items-center px-1.5 h-[18px] rounded text-[9.5px] font-bold uppercase tracking-wider ring-1 ring-emerald-200/30 text-emerald-50"
                          style={{ background: "rgba(0,0,0,0.25)", backdropFilter: "blur(4px)" }}
                          data-testid="ls-header-designation">
                      {session.agent_designation}
                    </span>
                  )}
                  {bothTyping && (
                    <span data-testid="ls-both-typing-badge"
                          className="inline-flex items-center gap-1 px-2 h-[20px] rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ring-emerald-200/40 text-white shadow-[0_2px_6px_rgba(0,0,0,0.25)]"
                          style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.55) 0%, rgba(5,150,105,0.55) 100%)", backdropFilter: "blur(6px)" }}
                          title="Both of you are typing right now">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inset-0 rounded-full bg-white opacity-90"
                              style={{ animation: "ls-ping-bi 1.2s cubic-bezier(0,0,0.2,1) infinite" }} />
                        <span className="relative h-1.5 w-1.5 rounded-full bg-white" />
                      </span>
                      Both typing
                    </span>
                  )}
                  {/* Verified badge — blue Twitter/Meta-style, hidden on very narrow */}
                  <svg width="14" height="14" viewBox="0 0 24 24" className="shrink-0 drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] hidden xs:inline">
                    <path fill="#3b82f6" stroke="#1e40af" strokeWidth="0.6" d="M12 1l2.39 2.45 3.41-.27.27 3.41L20.45 9 18 12l2.45 2.39-.27 3.41-3.41.27L14.39 22 12 19.55 9.61 22l-2.39-2.45-3.41-.27-.27-3.41L1 12l2.45-2.39-.27-3.41 3.41.27L9.61 4z" />
                    <path d="M8.5 12l2.3 2.3L15.5 9.5" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {/* Trustpilot-style 4.8 mini-rating — hidden on narrow phones (<400px) */}
                  <span className="hidden xs:inline-flex items-center gap-1 h-[20px] px-2 rounded-md ring-1 ring-white/15 shadow-[0_2px_6px_rgba(0,0,0,0.30)]"
                        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.55) 100%)", backdropFilter: "blur(6px)" }}
                        title="Rated 4.8 / 5 by users">
                    {/* Filled gold star — Trustpilot/Google review style */}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="#FBBF24" stroke="#92400E" strokeWidth="1.2" strokeLinejoin="round" className="shrink-0 drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <span className="text-[10.5px] font-black text-amber-100 leading-none tracking-tight" style={{ textShadow: "0 1px 1px rgba(0,0,0,0.5)" }}>4.8</span>
                    <span className="text-[8.5px] font-bold text-white/55 leading-none">/5</span>
                  </span>
                </div>
                <div className="text-[11px] truncate flex items-center gap-1.5 mt-0.5"
                     style={{ color: "rgba(236,253,245,0.92)" }}>
                  {!user ? <>
                    <span className="relative flex h-1.5 w-1.5 shrink-0">
                      <span className="absolute inset-0 rounded-full bg-emerald-200 animate-ping opacity-75" />
                      <span className="relative rounded-full h-1.5 w-1.5 bg-emerald-200" />
                    </span>
                    <span className="font-bold tracking-wide">Online</span>
                    <span className="opacity-50">·</span>
                    <span className="opacity-95">Replies in <b className="font-bold">~2 min</b></span>
                  </>
                    : session?.status === "claimed" && session?.agent_name ? <>
                      <span className="relative flex h-1.5 w-1.5 shrink-0">
                        <span className="absolute inset-0 rounded-full bg-emerald-200 animate-ping opacity-75" />
                        <span className="relative rounded-full h-1.5 w-1.5 bg-emerald-200" />
                      </span>
                      <b className="font-bold">{session.agent_name}</b>
                      <span className="opacity-50">·</span>
                      <span>typing now…</span>
                    </>
                    : session?.status === "open" && session?.needs_agent ? <>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                      এজেন্টের জন্য অপেক্ষা…
                    </>
                    : session?.status === "open" ? <>
                      <span className="relative flex h-1.5 w-1.5 shrink-0">
                        <span className="absolute inset-0 rounded-full bg-emerald-200 animate-ping opacity-75" />
                        <span className="relative rounded-full h-1.5 w-1.5 bg-emerald-200" />
                      </span>
                      <span className="font-bold tracking-wide">Online</span>
                      <span className="opacity-50">·</span>
                      <span>আপনার প্রশ্ন বেছে নিন</span>
                    </>
                    : session?.status === "closed" ? <>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#86efac" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      <span className="font-bold text-emerald-200">সমাধান হয়েছে — সেশন close</span>
                    </>
                    : "Always here to help"}
                </div>
              </div>
            </div>

            {/* Premium close button */}
            <button onClick={() => setOpen(false)} data-testid="live-support-close"
                    aria-label="Close chat"
                    className="h-9 w-9 rounded-xl bg-white/[0.10] hover:bg-white/[0.20] active:scale-90 flex items-center justify-center relative z-10 transition-all ring-1 ring-white/15 backdrop-blur-sm">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>

            {/* Local keyframe for ping if not already defined */}
            <style>{`
              @keyframes ls-ping {
                75%, 100% { transform: scale(2); opacity: 0; }
              }
              @keyframes ls-bot-pulse {
                0%   { transform: scale(1);    opacity: 0.55; }
                70%  { transform: scale(1.55); opacity: 0;    }
                100% { transform: scale(1.55); opacity: 0;    }
              }
              @keyframes ls-ping-bi {
                0%   { transform: scale(1);   opacity: 0.85; }
                80%  { transform: scale(2.2); opacity: 0;    }
                100% { transform: scale(2.2); opacity: 0;    }
              }
            `}</style>
          </div>

          {/* ──────────── Action bar (session-only) — mobile-optimised ──────────── */}
          {sessionId && (
            <div className="px-2 py-1.5 flex items-center gap-1.5 text-[11px] shrink-0 border-b border-white/[0.05] overflow-x-auto ls-no-scrollbar"
                 style={{ background: "rgba(255,255,255,0.02)" }}>
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                {topicHistory.length > 0 && session?.status !== "closed" && (
                  <button type="button" onClick={goBackOneStep} data-testid="ls-back-step"
                          aria-label="Back"
                          className="h-8 px-2 rounded-lg inline-flex items-center gap-1 bg-white/[0.04] hover:bg-white/[0.08] active:scale-95 border border-white/10 text-slate-300 font-bold transition-all shrink-0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                    <span className="hidden xs:inline">Back</span>
                  </button>
                )}
                {tree.length > 0 && session?.status !== "closed" && (
                  <button type="button" onClick={restartMenu} data-testid="ls-show-menu"
                          aria-label="Menu"
                          className="h-8 px-2 rounded-lg inline-flex items-center gap-1 bg-white/[0.04] hover:bg-white/[0.08] active:scale-95 border border-white/10 text-slate-300 font-bold transition-all shrink-0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
                    <span className="hidden xs:inline">Menu</span>
                  </button>
                )}
                <button type="button" onClick={() => setShowTickets((v) => !v)} data-testid="ls-my-tickets-toggle"
                        aria-label="My Tickets"
                        className={`h-8 px-2 rounded-lg inline-flex items-center gap-1 border transition-all font-bold active:scale-95 shrink-0 ${
                          showTickets
                            ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
                            : "bg-white/[0.04] hover:bg-white/[0.08] border-white/10 text-slate-300"
                        }`}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4z" /></svg>
                  <span className="hidden xs:inline">Tickets</span>
                  {(myTickets.counts?.pending || 0) > 0 && (
                    <span className="ml-0.5 px-1.5 h-4 rounded-full bg-amber-500/20 text-amber-300 text-[9px] font-black inline-flex items-center">{myTickets.counts.pending}</span>
                  )}
                </button>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {session?.status !== "closed" && (() => {
                  const armed = confirmCloseAt && Date.now() - confirmCloseAt < 5000;
                  return (
                    <button type="button" onClick={closeOwnSession} data-testid="ls-user-end-chat"
                            title={armed ? "আবার ক্লিক করুন" : "এই চ্যাট শেষ করুন"}
                            aria-label="End chat"
                            className={`h-8 px-2 rounded-lg inline-flex items-center gap-1 border font-bold transition-all active:scale-95 shrink-0 whitespace-nowrap ${
                              armed
                                ? "border-rose-300/70 bg-rose-500/30 hover:bg-rose-500/40 text-white animate-pulse"
                                : "border-rose-400/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200"
                            }`}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                      <span className="hidden xs:inline">{armed ? "নিশ্চিত করুন" : "চ্যাট বন্ধ"}</span>
                    </button>
                  );
                })()}
                <button type="button" onClick={toggleSound} data-testid="ls-user-sound-toggle"
                      title={soundOn ? "Notification mute করুন" : "Notification unmute করুন"}
                      aria-label="Toggle sound"
                      className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all active:scale-90 shrink-0 ${soundOn ? "text-emerald-300 hover:bg-emerald-400/10" : "text-slate-500 hover:bg-white/[0.06]"}`}>
                {soundOn ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
                )}
              </button>
              </div>
            </div>
          )}

          {/* ──────────── Body ──────────── */}
          {!user ? <LoginRequired /> :
           showTickets ? (
             <MyTicketsPanel
               items={myTickets.items}
               counts={myTickets.counts}
               onClose={() => setShowTickets(false)}
             />
           ) :
           activeForm ? (
             <FormSheet
               form={activeForm}
               currentUsername={user.username || ""}
               onCancel={() => setActiveForm(null)}
               onSubmit={(payload) => submitForm(activeForm.key, payload)}
             />
           ) : (
            <>
              <div ref={threadRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5" data-testid="live-support-thread"
                   style={{ background: "radial-gradient(ellipse at top, rgba(16,185,129,0.06) 0%, transparent 60%), #0a0f1c" }}>
                {!sessionId && (
                  <WelcomePanel
                    topics={tree}
                    onPick={(t) => startSession(t)}
                    onTalkToAgent={() => startSession({ id: null, label: "সরাসরি সাপোর্ট এজেন্ট" })}
                  />
                )}
                {sessionId && messages.map((m) => (
                  <MessageBubble key={m.id} m={m} onOption={onOptionClick} onImageOpen={setImagePreview} onFeedback={sendBotFeedback} feedbackBusy={feedbackBusy} />
                ))}
                {sessionId && session?.status === "closed" && ratingState && (
                  <RatingPanel
                    state={ratingState}
                    agentName={session?.agent_name}
                    onSubmit={submitRating}
                  />
                )}
                {sessionId && agentIsTyping && session?.status !== "closed" && (
                  <div className="flex items-end gap-2" data-testid="ls-user-agent-typing">
                    <div className="relative h-7 w-7 shrink-0">
                      {/* Outer pulse ring — signals "live typing" */}
                      <span aria-hidden className="absolute inset-0 rounded-lg bg-emerald-400/40"
                            style={{ animation: "ls-bot-pulse 1.4s cubic-bezier(0,0,0.2,1) infinite" }} />
                      <span aria-hidden className="absolute inset-0 rounded-lg bg-emerald-400/25"
                            style={{ animation: "ls-bot-pulse 1.4s cubic-bezier(0,0,0.2,1) 0.7s infinite" }} />
                      <div className="relative h-7 w-7 rounded-lg flex items-center justify-center text-white ring-1 ring-emerald-300/40 shadow-md shadow-emerald-900/40"
                           style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)" }}
                           aria-label="Support Bot typing">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1v-7h3v5z" /><path d="M3 19a2 2 0 0 0 2 2h1v-7H3v5z" /></svg>
                      </div>
                    </div>
                    <div className="px-3 py-2.5 rounded-2xl rounded-bl-sm bg-white/[0.06] border border-white/[0.08] inline-flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                )}
              </div>

              {/* ──────────── Composer ──────────── */}
              {sessionId && (
                <div className="border-t border-white/[0.06] shrink-0" style={{ background: "#0c1322" }}>
                  {/* Pending file previews */}
                  {pendingFiles.length > 0 && (
                    <div className="px-2.5 pt-2 flex flex-wrap gap-2" data-testid="ls-pending-files">
                      {pendingFiles.map((p) => (
                        <div key={p.key} className="relative h-16 w-16 rounded-lg overflow-hidden border border-white/10 bg-white/[0.04] group">
                          {p.previewUrl ? (
                            <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 text-[9px] px-1 text-center">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                              <span className="truncate w-full mt-1">{p.name}</span>
                            </div>
                          )}
                          {p.uploading && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                            </div>
                          )}
                          {p.error && (
                            <div className="absolute inset-0 bg-red-500/60 flex items-center justify-center text-white text-[10px] font-bold">FAIL</div>
                          )}
                          <button type="button" onClick={() => removePending(p.key)}
                                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-slate-900 border border-white/20 text-white text-[10px] flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <form onSubmit={(e) => { e.preventDefault(); sendText(); }}
                        className="px-2.5 py-2 flex items-end gap-2">
                    {/* Attach button */}
                    <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple
                           className="hidden" data-testid="ls-file-input"
                           onChange={(e) => { onPickFiles(e.target.files); e.target.value = ""; }} />
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                            disabled={session?.status === "closed"} data-testid="ls-attach"
                            title="Attach image or PDF"
                            className="h-10 w-10 rounded-full bg-white/[0.05] hover:bg-white/[0.10] disabled:opacity-40 border border-white/10 text-slate-300 hover:text-white flex items-center justify-center transition-colors shrink-0">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                    </button>

                    <input
                      value={input}
                      onChange={(e) => { setInput(e.target.value); if (e.target.value) pingTyping(); }}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                      placeholder={session?.status === "closed" ? "Session closed" : "Type a message…"}
                      disabled={session?.status === "closed" || sending}
                      data-testid="ls-input"
                      className="flex-1 h-10 px-4 rounded-full border border-white/10 bg-white/[0.05] text-sm focus:outline-none focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/15 text-white placeholder-slate-500 disabled:opacity-50 transition-all"
                    />
                    <button type="submit" disabled={(!input.trim() && pendingFiles.filter((p) => p.url).length === 0) || sending || session?.status === "closed"}
                            data-testid="ls-send"
                            className="h-10 w-10 rounded-full disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center shadow-lg shadow-emerald-500/20 active:scale-95 transition-transform shrink-0"
                            style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                            aria-label="Send">
                      {sending ? (
                        <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      ) : (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </>
           )}
        </div>
      )}
    </>
  );
}

// ─────────────── Sub-components ───────────────

function LoginRequired() {
  const BENGALI_FONT = "'Hind Siliguri', 'Noto Sans Bengali', system-ui, sans-serif";
  return (
    <div className="relative flex-1 flex flex-col items-stretch overflow-hidden" data-testid="ls-login-required"
         style={{
           background:
             "radial-gradient(ellipse 700px 360px at 50% -5%, rgba(16,185,129,0.16) 0%, transparent 55%)," +
             "radial-gradient(ellipse 450px 300px at 90% 110%, rgba(59,130,246,0.08) 0%, transparent 65%)," +
             "radial-gradient(ellipse 380px 260px at 5% 100%, rgba(139,92,246,0.06) 0%, transparent 65%)," +
             "linear-gradient(180deg, #050a14 0%, #03060c 100%)",
         }}>

      {/* Soft dot grid (much more subtle than full grid) */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.18]"
           style={{
             backgroundImage:
               "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.18) 1px, transparent 1.5px)",
             backgroundSize: "28px 28px",
             maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black 30%, transparent 75%)",
             WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black 30%, transparent 75%)",
           }} />

      {/* Floating ambient glow */}
      <div aria-hidden className="absolute -top-20 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full pointer-events-none"
           style={{
             background: "radial-gradient(circle, rgba(16,185,129,0.20) 0%, transparent 65%)",
             animation: "ls-orbit-1 10s ease-in-out infinite",
             filter: "blur(20px)",
           }} />

      <div className="relative flex-1 flex flex-col items-center justify-center px-5 py-6">

        {/* ═══════════ Hero icon block ═══════════ */}
        <div className="relative mb-6" style={{ animation: "ls-fade-up 0.6s ease-out both" }}>
          {/* outer breathing ring */}
          <span aria-hidden className="absolute inset-[-32px] rounded-full pointer-events-none border border-emerald-400/10"
                style={{ animation: "ls-ring 3s ease-out infinite" }} />
          {/* mid ring */}
          <span aria-hidden className="absolute inset-[-18px] rounded-full pointer-events-none border border-emerald-400/20"
                style={{ animation: "ls-ring 3s ease-out infinite 0.6s" }} />
          {/* radial glow background */}
          <span aria-hidden className="absolute inset-[-26px] rounded-full pointer-events-none -z-10"
                style={{ background: "radial-gradient(circle, rgba(16,185,129,0.22) 0%, transparent 60%)", filter: "blur(8px)" }} />

          {/* avatar disc */}
          <div className="relative h-[104px] w-[104px] rounded-full flex items-center justify-center ring-1 ring-emerald-400/30"
               style={{
                 background:
                   "radial-gradient(circle at 28% 22%, rgba(110,231,183,0.30) 0%, rgba(16,185,129,0.12) 45%, rgba(4,18,32,0.85) 100%)",
                 boxShadow:
                   "0 20px 48px -16px rgba(16,185,129,0.55)," +
                   "inset 0 1px 0 rgba(255,255,255,0.10)," +
                   "inset 0 -10px 24px rgba(0,0,0,0.5)",
                 backdropFilter: "blur(8px)",
               }}>
            {/* top shine */}
            <span aria-hidden className="absolute top-1.5 left-3 h-8 w-8 rounded-full opacity-60 pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%)" }} />
            {/* Conic gradient ring */}
            <span aria-hidden className="absolute inset-[-2px] rounded-full pointer-events-none -z-10"
                  style={{
                    background:
                      "conic-gradient(from 200deg, transparent 0deg, rgba(52,211,153,0.55) 90deg, transparent 180deg, rgba(16,185,129,0.4) 270deg, transparent 360deg)",
                    animation: "ls-spin 6s linear infinite",
                    filter: "blur(4px)",
                  }} />
            {/* Headset icon */}
            <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="#6ee7b7" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="relative drop-shadow-[0_2px_8px_rgba(16,185,129,0.45)]">
              <path d="M3 14v-3a9 9 0 0 1 18 0v3" />
              <path d="M21 19a2 2 0 0 1-2 2h-1v-7h3v5z" />
              <path d="M3 19a2 2 0 0 0 2 2h1v-7H3v5z" />
            </svg>
            {/* online dot */}
            <span className="absolute bottom-1.5 right-1.5 h-4 w-4 rounded-full bg-emerald-400 ring-[3px] ring-[#04080f] shadow-[0_0_12px_rgba(16,185,129,0.7)]">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
            </span>
          </div>
        </div>

        {/* ═══════════ Online team strip — centered, single column ═══════════ */}
        <div className="flex flex-col items-center gap-2.5 mb-5" style={{ animation: "ls-fade-up 0.6s ease-out 0.1s both" }}>
          {/* avatar stack — Bangladeshi / South-Asian customer-service agents (6 people) */}
          <div className="flex items-center -space-x-2.5">
            {[
              // Curated Bangladeshi / South-Asian portraits.
              // NOTE: Admin can replace these via the future "Live Chat Team Avatars" config.
              { name: "Rifa",   src: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=140&h=140&fit=crop&crop=faces&q=85" },
              { name: "Akib",   src: "https://images.unsplash.com/photo-1611432579402-7037e3e2c1e4?w=140&h=140&fit=crop&crop=faces&q=85" },
              { name: "Sumi",   src: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=140&h=140&fit=crop&crop=faces&q=85" },
              { name: "Mahin",  src: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=140&h=140&fit=crop&crop=faces&q=85" },
              { name: "Tania",  src: "https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=140&h=140&fit=crop&crop=faces&q=85" },
              { name: "Sajid",  src: "https://images.unsplash.com/photo-1568585105565-e372998a695a?w=140&h=140&fit=crop&crop=faces&q=85" },
            ].map((a, i) => (
              <span key={i}
                    title={a.name}
                    className="relative h-9 w-9 rounded-full overflow-hidden ring-[2.5px] ring-[#03060c] shadow-[0_6px_14px_-4px_rgba(0,0,0,0.7)]"
                    style={{ zIndex: 20 - i }}>
                <img src={a.src} alt={a.name} loading="lazy"
                     className="h-full w-full object-cover"
                     onError={(e) => {
                       e.currentTarget.style.display = "none";
                       e.currentTarget.parentElement.style.background =
                         `linear-gradient(135deg, hsl(${(i * 80) % 360},70%,55%), hsl(${(i * 80 + 40) % 360},70%,40%))`;
                       e.currentTarget.parentElement.insertAdjacentHTML(
                         "beforeend",
                         `<span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;font-size:12px">${a.name.charAt(0)}</span>`
                       );
                     }} />
              </span>
            ))}
          </div>
          {/* Combined status + avg-reply pill */}
          <div className="inline-flex items-center gap-2 px-3 h-7 rounded-full border border-emerald-400/30 bg-emerald-500/[0.07] backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
              <span className="relative rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[10.5px] font-bold text-emerald-200 tracking-wide">
              ১০+ সাপোর্ট এজেন্ট অনলাইন
            </span>
            <span className="text-emerald-400/40">·</span>
            <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-emerald-200/90">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              ~২ মিনিটে উত্তর
            </span>
          </div>
        </div>

        {/* ═══════════ Title & subtitle ═══════════ */}
        <div style={{ animation: "ls-fade-up 0.6s ease-out 0.15s both" }} className="text-center">
          <h2 className="text-[20px] sm:text-[22px] font-black text-white tracking-tight leading-[1.15]"
              style={{ fontFamily: BENGALI_FONT }}>
            চলুন, কথা বলি
            <span className="ml-1.5 inline-block" style={{ animation: "ls-wave 1.6s ease-in-out infinite", transformOrigin: "70% 70%" }}>👋</span>
          </h2>
          <p className="mt-2 text-[12.5px] text-slate-400 max-w-[290px] mx-auto leading-relaxed"
             style={{ fontFamily: BENGALI_FONT }}>
            লগইন করে চ্যাট শুরু করুন — আমাদের সাপোর্ট টিম আপনাকে সাহায্য করতে প্রস্তুত।
          </p>
        </div>

        {/* ═══════════ Feature pills row — symmetric 3-pill grid ═══════════ */}
        <div className="mt-5 grid grid-cols-3 gap-2 w-full max-w-[320px]"
             style={{ animation: "ls-fade-up 0.6s ease-out 0.25s both" }}>
          <FeaturePill icon={
            <><circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" /></>
          } color="#34d399" label="২৪/৭ সাপোর্ট" font={BENGALI_FONT} />
          <FeaturePill icon={
            <><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" /></>
          } color="#60a5fa" label="বাংলা / English" font={BENGALI_FONT} />
          <FeaturePill icon={
            <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          } color="#fbbf24" label="দ্রুত Reply" font={BENGALI_FONT} />
        </div>

        {/* ═══════════ Primary CTA ═══════════ */}
        <a href="/login" data-testid="ls-go-login"
           className="mt-7 group relative h-12 w-full max-w-[320px] rounded-full text-white font-bold text-[14px] inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98] hover:scale-[1.02] overflow-hidden"
           style={{
             background:
               "linear-gradient(135deg, #10b981 0%, #059669 55%, #047857 100%)",
             boxShadow:
               "0 14px 36px -10px rgba(16,185,129,0.6)," +
               "0 0 0 1px rgba(110,231,183,0.4)," +
               "inset 0 1px 0 rgba(255,255,255,0.3)",
             fontFamily: BENGALI_FONT,
             animation: "ls-fade-up 0.6s ease-out 0.3s both",
           }}>
          {/* Shimmer sweep */}
          <span aria-hidden className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] ease-out pointer-events-none"
                style={{ background: "linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.28) 50%, transparent 65%)" }} />
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="relative">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          <span className="relative">লগইন করে চ্যাট শুরু করুন</span>
        </a>

        {/* ═══════════ Secondary actions ═══════════ */}
        <div className="mt-3.5 flex items-center gap-4 text-[12px]"
             style={{ animation: "ls-fade-up 0.6s ease-out 0.35s both", fontFamily: BENGALI_FONT }}>
          <a href="/signup" data-testid="ls-go-signup"
             className="text-slate-400 hover:text-emerald-300 transition-colors font-semibold inline-flex items-center gap-1">
            নতুন একাউন্ট
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
          <span className="h-3 w-px bg-white/10" />
          <a href="/help" className="text-slate-400 hover:text-emerald-300 transition-colors font-semibold inline-flex items-center gap-1">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            FAQ দেখুন
          </a>
        </div>
      </div>

      {/* ═══════════ Footer trust strip ═══════════ */}
      <div className="relative px-5 py-2.5 border-t border-white/[0.05] flex items-center justify-center gap-3 text-[10.5px] text-slate-500 shrink-0"
           style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}>
        <span className="inline-flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span className="text-emerald-400/95 font-bold tracking-wide">SSL SECURED</span>
        </span>
        <span className="opacity-30">·</span>
        <span className="font-semibold tracking-wide">END-TO-END ENCRYPTED</span>
        <span className="opacity-30">·</span>
        <span className="text-slate-400 font-bold tracking-wide">MAXLIVE88</span>
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes ls-ring {
          0%, 100% { transform: scale(1);   opacity: 0.55; }
          50%      { transform: scale(1.18); opacity: 0;    }
        }
        @keyframes ls-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes ls-fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ls-orbit-1 {
          0%, 100% { transform: translate(-50%, 0) translateY(0); }
          50%      { transform: translate(-50%, 0) translateY(-12px); }
        }
        @keyframes ls-wave {
          0%, 60%, 100% { transform: rotate(0deg); }
          10%, 30%      { transform: rotate(14deg); }
          20%           { transform: rotate(-8deg); }
        }
      `}</style>
    </div>
  );
}

function FeaturePill({ icon, color, label, font }) {
  return (
    <span className="inline-flex items-center justify-center gap-1.5 h-8 px-2 rounded-full border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm text-[10.5px] font-bold text-slate-200 hover:border-white/[0.16] hover:bg-white/[0.06] transition-colors whitespace-nowrap"
          style={{ fontFamily: font }}>
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
        {icon}
      </svg>
      {label}
    </span>
  );
}

function WelcomePanel({ topics, onPick, onTalkToAgent }) {
  return (
    <>
      <div className="flex gap-2">
        <div className="relative h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-white ring-1 ring-white/10 shadow-lg shadow-emerald-900/30"
             style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)" }}
             aria-label="Maxlive88 Support">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1v-7h3v5z" /><path d="M3 19a2 2 0 0 0 2 2h1v-7H3v5z" /></svg>
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-900" aria-label="online" />
        </div>
        <div className="rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-[13px] text-slate-100 leading-relaxed flex-1 min-w-0 border border-white/[0.08]"
             style={{ background: "linear-gradient(180deg, rgba(16,185,129,0.10) 0%, rgba(255,255,255,0.02) 100%)" }}>
          <div className="font-black text-white">Maxlive88 — স্বাগতম! 👋</div>
          <div className="text-[12.5px] mt-0.5 text-slate-300">আমরা আপনাকে কীভাবে সাহায্য করতে পারি?</div>
        </div>
      </div>

      {/* Direct-to-agent CTA — primary action */}
      <button
        type="button"
        onClick={onTalkToAgent}
        data-testid="ls-talk-to-agent"
        className="group relative w-full mt-3 p-[1.5px] rounded-2xl overflow-hidden transition-transform active:scale-[0.98]"
        style={{ background: "linear-gradient(135deg, #34d399 0%, #10b981 45%, #047857 100%)" }}
      >
        <span className="pointer-events-none absolute -inset-x-12 -top-12 h-28 rotate-12 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)" }} />
        <div className="relative rounded-[14px] px-3.5 py-3 flex items-center gap-3"
             style={{ background: "linear-gradient(180deg, rgba(6,78,59,0.92) 0%, rgba(4,47,46,0.96) 100%)" }}>
          <div className="relative h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ring-1 ring-emerald-300/30"
               style={{ background: "linear-gradient(135deg, rgba(52,211,153,0.25) 0%, rgba(16,185,129,0.10) 100%)" }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#6ee7b7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
              <path d="M21 14a3 3 0 0 1-3 3h-1v-6h1a3 3 0 0 1 3 3z" />
              <path d="M3 14a3 3 0 0 0 3 3h1v-6H6a3 3 0 0 0-3 3z" />
              <path d="M21 16v1a4 4 0 0 1-4 4h-3" />
            </svg>
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-300 ring-2 ring-emerald-900 animate-pulse" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <div className="text-[13px] sm:text-[13.5px] font-black text-white tracking-tight leading-tight">সরাসরি সাপোর্ট এজেন্টের সাথে কথা বলুন</div>
            <div className="text-[10.5px] sm:text-[11px] text-emerald-200/90 mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse shrink-0" />
                ২৪/৭ লাইভ এজেন্ট
              </span>
              <span className="opacity-50">•</span>
              <span>গড় উত্তর &lt; ১ মিনিট</span>
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a7f3d0" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 transition-transform group-hover:translate-x-0.5">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </button>

      {topics.length > 0 && (
        <>
          <div className="flex items-center gap-3 pt-4 pb-1">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.18em]">অথবা টপিক বেছে নিন</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {topics.map((t) => (
              <button key={t.id} type="button" onClick={() => onPick(t)}
                      data-testid={`ls-topic-${t.id}`}
                      className="group text-left px-2.5 py-2.5 rounded-xl border border-white/[0.08] bg-white/[0.04] hover:bg-emerald-500/[0.10] hover:border-emerald-400/40 active:scale-[0.98] text-[12px] sm:text-[13px] font-semibold text-slate-200 hover:text-emerald-200 transition-all shadow-sm min-h-[44px] flex items-center">
                <span className="line-clamp-2 leading-tight break-words">{t.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function MessageBubble({ m, onOption, onImageOpen, onFeedback, feedbackBusy }) {
  if (m.sender_type === "system") {
    return (
      <div className="flex gap-2" data-testid="ls-msg-system">
        <div className="relative h-7 w-7 rounded-lg flex items-center justify-center shrink-0 text-white ring-1 ring-white/10 shadow-md shadow-emerald-900/30"
             style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)" }}
             aria-label="Support Bot">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1v-7h3v5z" /><path d="M3 19a2 2 0 0 0 2 2h1v-7H3v5z" /></svg>
        </div>
        <div className="max-w-[85%] sm:max-w-[80%] min-w-0">
          <div className="border border-white/[0.08] text-slate-100 rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-[13.5px] leading-[1.55] break-words shadow-sm"
               style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)" }}>
            <RichText text={m.text} />
          </div>
          {(m.options && m.options.length > 0) && (
            <div className="mt-2 space-y-1.5">
              {m.options.map((o, idx) => {
                const isAgentEscalation = o.action === "call_agent";
                const cls = isAgentEscalation
                  ? "block w-full text-left px-3 py-2.5 rounded-xl border border-rose-400/40 bg-rose-500/[0.10] hover:bg-rose-500/[0.20] active:scale-[0.98] hover:border-rose-400/70 text-[13px] font-bold text-rose-100 transition-all shadow-[0_2px_10px_-2px_rgba(244,63,94,0.30)] min-h-[44px]"
                  : "block w-full text-left px-3 py-2.5 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.08] hover:bg-emerald-500/[0.18] active:scale-[0.98] hover:border-emerald-400/60 text-[12.5px] sm:text-[13px] font-bold text-emerald-200 transition-all min-h-[42px]";
                return (
                  <button key={idx} type="button" onClick={() => onOption(o)}
                          data-testid={`ls-opt-${o.action || o.id || o.form_key || idx}`}
                          className={cls}>
                    <span className="inline-flex items-start gap-2 w-full">
                      {isAgentEscalation ? (
                        <span className="relative flex h-2 w-2 mt-1.5 shrink-0">
                          <span className="absolute inset-0 rounded-full bg-rose-300 animate-ping opacity-75" />
                          <span className="relative rounded-full h-2 w-2 bg-rose-300" />
                        </span>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="mt-1 shrink-0"><polyline points="9 18 15 12 9 6" /></svg>
                      )}
                      <span className="flex-1 leading-snug break-words">{o.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {/* 👍 / 👎 feedback row — only on Maxlive88 Auto-Assist bot bubbles.
              When the user has already voted, freeze the buttons and show
              the chosen state in a brighter tone. */}
          {m.sender_name === "Maxlive88 Auto-Assist" && onFeedback && (
            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-500" data-testid={`ls-bot-feedback-${m.id}`}>
              <span className="opacity-80">এই উত্তর কেমন?</span>
              <button
                type="button"
                onClick={() => onFeedback(m.id, "up")}
                disabled={!!(feedbackBusy && feedbackBusy[m.id]) || !!m.feedback}
                data-testid={`ls-bot-feedback-up-${m.id}`}
                className={`px-2 py-1 rounded-md border transition-all ${
                  (m.feedback?.rating === "up")
                    ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200"
                    : "bg-white/[0.03] border-white/10 text-slate-400 hover:bg-emerald-500/[0.10] hover:border-emerald-400/40 hover:text-emerald-200"
                } ${m.feedback ? "cursor-default" : ""}`}
                title="ভালো লেগেছে"
              >
                <span className="inline-flex items-center gap-1">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill={m.feedback?.rating === "up" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h3.5l3-7a1.5 1.5 0 0 1 1.5 0 2 2 0 0 1 0 2.88Z" /></svg>
                  <span>উপকারী</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => onFeedback(m.id, "down")}
                disabled={!!(feedbackBusy && feedbackBusy[m.id]) || !!m.feedback}
                data-testid={`ls-bot-feedback-down-${m.id}`}
                className={`px-2 py-1 rounded-md border transition-all ${
                  (m.feedback?.rating === "down")
                    ? "bg-rose-500/20 border-rose-400/50 text-rose-200"
                    : "bg-white/[0.03] border-white/10 text-slate-400 hover:bg-rose-500/[0.10] hover:border-rose-400/40 hover:text-rose-200"
                } ${m.feedback ? "cursor-default" : ""}`}
                title="অপ্রাসঙ্গিক / ভুল উত্তর"
              >
                <span className="inline-flex items-center gap-1">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill={m.feedback?.rating === "down" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2" /><path d="m9 18.12 1-4.12H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-3.5l-3 7a1.5 1.5 0 0 1-1.5 0 2 2 0 0 1 0-2.88Z" /></svg>
                  <span>ঠিক হয়নি</span>
                </span>
              </button>
              {m.feedback?.rating === "up" && (
                <span className="text-emerald-300 ml-1">✓ ধন্যবাদ!</span>
              )}
              {m.feedback?.rating === "down" && (
                <span className="text-rose-300 ml-1">আমরা উন্নতি করব 🙏</span>
              )}
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-1">{fmtTime(m.created_at)}</div>
        </div>
      </div>
    );
  }
  const isUser = m.sender_type === "user";
  const isAgent = m.sender_type === "agent";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2`} data-testid={`ls-msg-${m.sender_type}`}>
      {!isUser && (
        isAgent && m.sender_avatar ? (
          <img
            src={fileUrl(m.sender_avatar)}
            alt={m.sender_name || "Agent"}
            className="h-7 w-7 rounded-lg object-cover shrink-0 ring-1 ring-emerald-300/50 self-end"
            data-testid="ls-msg-agent-avatar"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="h-7 w-7 rounded-lg flex items-center justify-center font-bold text-[10px] shrink-0 text-white ring-1 ring-white/10 self-end"
               style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)" }}>
            {(m.sender_name || "A").charAt(0).toUpperCase()}
          </div>
        )
      )}
      <div className={`max-w-[85%] sm:max-w-[78%] min-w-0 ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        {!isUser && m.sender_name && (
          <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-[0.12em] mb-1 px-1 truncate max-w-full">{m.sender_name}</div>
        )}
        {m.text && (
          <div className={`px-3.5 py-2.5 text-[13.5px] leading-[1.6] break-words shadow-sm ${
            isUser
              ? "rounded-2xl rounded-br-sm text-white whitespace-pre-wrap"
              : "rounded-2xl rounded-bl-sm border border-white/[0.08] text-slate-100"
          }`}
               style={
                 isUser
                   ? { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", boxShadow: "0 2px 8px -2px rgba(16,185,129,0.35)" }
                   : { background: "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)" }
               }>
            {isUser ? m.text : <RichText text={m.text} />}
          </div>
        )}
        {/* attachments */}
        {m.attachments && m.attachments.length > 0 && (
          <div className={`mt-1.5 grid ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-1.5 w-full`}>
            {m.attachments.map((a, idx) => {
              const url = a.url.startsWith("http") ? a.url : `${BACKEND_URL}${a.url}`;
              const isImage = (a.mime || "").startsWith("image/");
              return isImage ? (
                <button key={idx} type="button" onClick={() => onImageOpen && onImageOpen(url)}
                        className="block rounded-xl overflow-hidden border border-white/10 bg-white/[0.04] hover:opacity-90 transition-opacity active:scale-[0.98]">
                  <img src={url} alt={a.name} className="w-full h-32 object-cover" />
                </button>
              ) : (
                <a key={idx} href={url} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
                  <div className="p-2.5 text-[11px] text-slate-300 flex items-center gap-1.5 h-24">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-slate-400"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                    <span className="truncate">{a.name}</span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
        <div className={`text-[10px] text-slate-500 mt-1 px-1 ${isUser ? "text-right" : ""}`}>{fmtTime(m.created_at)}</div>
      </div>
    </div>
  );
}

function FormSheet({ form, currentUsername, onCancel, onSubmit }) {
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Prefill any field named like username from current player
  useEffect(() => {
    const pf = {};
    form.fields.forEach((f) => {
      if (["username", "user_name", "user"].includes(f.key) && currentUsername) {
        pf[f.key] = currentUsername;
      }
    });
    if (Object.keys(pf).length) setValues((v) => ({ ...v, ...pf }));
  }, [form, currentUsername]);

  const setVal = (k, v, t) => {
    let val = v;
    if (t === "uppercase") val = val.toUpperCase();
    if (t === "lowercase") val = val.toLowerCase();
    setValues((s) => ({ ...s, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    form.fields.forEach((f) => {
      if (!f.required) return;
      if (f.type === "file") {
        if (!files[f.key]) errs[f.key] = "Required";
      } else {
        if (!values[f.key] || !String(values[f.key]).trim()) errs[f.key] = "Required";
      }
    });
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await onSubmit({ fields: values, files });
    } catch (err) {
      const msg = err?.response?.data?.detail || "সাবমিট হয়নি, আবার চেষ্টা করুন";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-white overflow-hidden" data-testid="ls-form-sheet">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-bold text-gray-900 truncate">📋 {form.title}</div>
          <div className="text-[11px] text-gray-500 truncate">সমস্ত তথ্য সঠিকভাবে দিন</div>
        </div>
        <button onClick={onCancel} data-testid="ls-form-cancel" className="text-gray-500 hover:text-gray-800 text-sm">✕</button>
      </div>

      <form onSubmit={submit} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {form.description && (
          <div className="text-[12px] text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-3 whitespace-pre-wrap leading-relaxed">
            {form.description}
          </div>
        )}
        {form.fields.map((f) => (
          <div key={f.key} data-testid={`ls-field-${f.key}`}>
            <label className="block text-[13px] font-bold text-gray-900 mb-0.5">
              {f.label} {f.required && <span className="text-red-500">*</span>}
            </label>
            {f.hint && <div className="text-[11px] text-gray-500 mb-1.5 whitespace-pre-wrap">{f.hint}</div>}
            {f.type === "file" ? (
              <FileField
                accept={f.accept}
                value={files[f.key]}
                onChange={(file) => setFiles((s) => ({ ...s, [f.key]: file }))}
              />
            ) : f.type === "textarea" ? (
              <textarea
                rows={3}
                value={values[f.key] || ""}
                onChange={(e) => setVal(f.key, e.target.value, f.transform)}
                placeholder={f.placeholder || ""}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-emerald-500 resize-none text-gray-900 placeholder-gray-400 bg-white"
              />
            ) : (
              <input
                type={f.type === "number" ? "number" : "text"}
                value={values[f.key] || ""}
                onChange={(e) => setVal(f.key, e.target.value, f.transform)}
                placeholder={f.placeholder || ""}
                className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-emerald-500 text-gray-900 placeholder-gray-400 bg-white"
              />
            )}
            {errors[f.key] && <div className="text-[11px] text-red-500 mt-1">{errors[f.key]}</div>}
          </div>
        ))}
      </form>

      <div className="border-t border-gray-100 p-3 flex gap-2 bg-white">
        <button type="button" onClick={onCancel} className="h-11 px-4 rounded-lg border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50">
          বাতিল
        </button>
        <button type="button" onClick={submit} disabled={submitting} data-testid="ls-form-submit"
                className="flex-1 h-11 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-sm">
          {submitting ? "জমা দেওয়া হচ্ছে..." : (form.submit_label || "সাবমিট করুন")}
        </button>
      </div>
    </div>
  );
}

function FileField({ accept, value, onChange }) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [sizeError, setSizeError] = useState("");
  const MAX_BYTES = 8 * 1024 * 1024; // 8 MB

  // Build/clear an object-URL preview whenever the selected image changes.
  useEffect(() => {
    if (value && value.type && value.type.startsWith("image/")) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl("");
  }, [value]);

  const openPicker = () => {
    // Reset input value first so re-selecting the SAME file still fires onChange.
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.click();
  };

  const handlePick = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setSizeError(`ফাইল অনেক বড় (${(file.size / 1024 / 1024).toFixed(2)} MB)। সর্বোচ্চ 8 MB অনুমোদিত।`);
      // Clear input so user can re-pick after fixing
      if (inputRef.current) inputRef.current.value = "";
      onChange(null);
      return;
    }
    setSizeError("");
    onChange(file);
  };

  const remove = () => {
    setSizeError("");
    if (inputRef.current) inputRef.current.value = "";
    onChange(null);
  };

  const isImage = value && value.type && value.type.startsWith("image/");
  const isPdf = value && (value.type === "application/pdf" || /\.pdf$/i.test(value.name || ""));
  const sizeKb = value ? (value.size / 1024).toFixed(0) : 0;
  const sizeLabel = value ? (value.size > 1024 * 1024 ? `${(value.size / 1024 / 1024).toFixed(2)} MB` : `${sizeKb} KB`) : "";

  return (
    <div data-testid="ls-file-field">
      <input
        ref={inputRef}
        type="file"
        accept={accept || "image/*,application/pdf"}
        onChange={handlePick}
        className="hidden"
        data-testid="ls-file-input"
      />

      {!value ? (
        // ── Empty state: dashed drop-area ──
        <button
          type="button"
          onClick={openPicker}
          data-testid="ls-file-browse-btn"
          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
        >
          <div className="text-3xl mb-1">📎</div>
          <div className="text-[13px] font-semibold text-gray-700">একটি ফাইল ব্রাউজ করুন</div>
          <div className="text-[11px] text-gray-500 mt-0.5">JPG, JPEG, PNG, PDF · max 8 MB</div>
        </button>
      ) : (
        // ── Selected state: preview card + Change/Remove ──
        <div
          className="w-full border-2 border-emerald-400 bg-emerald-50/40 rounded-lg p-3"
          data-testid="ls-file-selected"
        >
          <div className="flex items-start gap-3">
            {isImage && previewUrl ? (
              <img
                src={previewUrl}
                alt="preview"
                className="w-20 h-20 object-cover rounded-lg border border-emerald-200 flex-shrink-0"
                data-testid="ls-file-preview-img"
              />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-white border border-emerald-200 flex items-center justify-center text-3xl flex-shrink-0">
                {isPdf ? "📄" : "📎"}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 text-[12px] font-bold text-emerald-700 mb-0.5">
                <span>✅</span><span>আপলোড প্রস্তুত</span>
              </div>
              <div
                className="text-[12px] font-semibold text-gray-800 break-all line-clamp-2"
                data-testid="ls-file-name"
                title={value.name}
              >
                {value.name}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5" data-testid="ls-file-size">
                {sizeLabel}
              </div>

              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={openPicker}
                  data-testid="ls-file-change-btn"
                  className="px-3 h-7 rounded-md bg-white border border-emerald-300 text-emerald-700 text-[11px] font-bold hover:bg-emerald-50"
                >
                  🔄 পরিবর্তন করুন
                </button>
                <button
                  type="button"
                  onClick={remove}
                  data-testid="ls-file-remove-btn"
                  className="px-3 h-7 rounded-md bg-white border border-red-200 text-red-600 text-[11px] font-bold hover:bg-red-50"
                >
                  ✕ সরান
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {sizeError && (
        <div
          className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 mt-2"
          data-testid="ls-file-size-error"
        >
          ⚠️ {sizeError}
        </div>
      )}
    </div>
  );
}

function RatingPanel({ state, agentName, onSubmit }) {
  const [score, setScore] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(!state?.can_rate && !!state?.rating);

  if (done || state?.rating) {
    const r = state.rating;
    return (
      <div className="bg-gradient-to-r from-emerald-50 to-transparent border border-emerald-200 rounded-2xl p-3 mt-2" data-testid="ls-rating-done">
        <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-1">আপনার রেটিং</div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <span key={i} className={`text-lg ${i <= (r?.score || 0) ? "text-amber-400" : "text-gray-300"}`}>★</span>
          ))}
        </div>
        {r?.comment && <div className="text-[12px] text-gray-700 italic mt-1">&ldquo;{r.comment}&rdquo;</div>}
        <div className="text-[10px] text-gray-500 mt-1">ধন্যবাদ! আপনার মতামত গৃহীত হয়েছে।</div>
      </div>
    );
  }

  if (!state?.can_rate) return null;

  const submit = async () => {
    if (!score) return;
    setSaving(true);
    try {
      await onSubmit(score, comment.trim());
      setDone(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Rating submit failed";
      alert(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border-2 border-amber-200 rounded-2xl p-3 mt-2 shadow-sm" data-testid="ls-rating-prompt">
      <div className="text-[13px] font-bold text-gray-900 mb-1">⭐ আপনার অভিজ্ঞতা কেমন ছিল?</div>
      <div className="text-[11px] text-gray-500 mb-2">
        {agentName ? <>এজেন্ট <b>{agentName}</b> কে রেটিং দিন</> : "আমাদের সাপোর্ট কোয়ালিটি উন্নত করতে সাহায্য করুন"}
      </div>
      <div className="flex items-center gap-1.5 mb-2.5" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => setScore(i)}
            onMouseEnter={() => setHover(i)}
            data-testid={`ls-rate-star-${i}`}
            className="text-3xl leading-none transition-transform active:scale-90 hover:scale-110"
            aria-label={`${i} star`}
          >
            <span className={i <= (hover || score) ? "text-amber-400" : "text-gray-300"}>★</span>
          </button>
        ))}
        {score > 0 && (
          <span className="ml-1 text-[11px] font-bold text-gray-600">
            {score === 5 ? "চমৎকার!" : score === 4 ? "ভাল" : score === 3 ? "মাঝারি" : score === 2 ? "দুর্বল" : "অসন্তোষজনক"}
          </span>
        )}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        placeholder="মতামত (ঐচ্ছিক)"
        data-testid="ls-rate-comment"
        className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 resize-none text-gray-900 placeholder-gray-400 bg-white"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!score || saving}
        data-testid="ls-rate-submit"
        className="mt-2 w-full h-10 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white text-sm font-bold"
      >
        {saving ? "জমা দেওয়া হচ্ছে..." : "রেটিং সাবমিট করুন"}
      </button>
    </div>
  );
}



const TICKET_STATUS_STYLE = {
  pending:     { l: "Pending",     bg: "bg-amber-500/15 text-amber-300 border-amber-400/30", dot: "#f59e0b" },
  in_progress: { l: "In Progress", bg: "bg-blue-500/15 text-blue-300 border-blue-400/30",   dot: "#3b82f6" },
  resolved:    { l: "Resolved",    bg: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30", dot: "#10b981" },
  rejected:    { l: "Rejected",    bg: "bg-red-500/15 text-red-300 border-red-400/30",       dot: "#ef4444" },
};

function MyTicketsPanel({ items, counts, onClose }) {
  const total = (counts?.pending || 0) + (counts?.in_progress || 0) + (counts?.resolved || 0) + (counts?.rejected || 0);
  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="ls-my-tickets-panel"
         style={{ background: "radial-gradient(ellipse at top, rgba(16,185,129,0.06) 0%, transparent 60%), #0a0f1c" }}>
      {/* Sub-header */}
      <div className="px-3 py-2.5 border-b border-white/[0.05] flex items-center justify-between shrink-0">
        <div>
          <div className="text-[13px] font-black text-white tracking-tight">📋 আমার টিকেট</div>
          <div className="text-[10px] text-slate-500">{total} টিকেট · {counts?.pending || 0} pending · {counts?.resolved || 0} resolved</div>
        </div>
        <button onClick={onClose} data-testid="ls-my-tickets-close"
                className="h-8 px-2.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-300">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
          চ্যাটে ফিরে যান
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
        {items.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-12">
            <div className="h-14 w-14 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4z" /></svg>
            </div>
            <div className="text-[13px] font-bold text-slate-300">এখনো কোনো টিকেট নেই</div>
            <div className="text-[11px] text-slate-500 mt-1 max-w-[240px]">কোনো ফর্ম সাবমিট করলে এখানে দেখতে পাবেন</div>
          </div>
        ) : items.map((t) => (
          <UserTicketRow key={t.id} t={t} />
        ))}
      </div>
    </div>
  );
}

function UserTicketRow({ t }) {
  const [expanded, setExpanded] = useState(false);
  const sm = TICKET_STATUS_STYLE[t.status] || TICKET_STATUS_STYLE.pending;
  const tno = t.ticket_no || "—";
  const isFinal = t.status === "resolved" || t.status === "rejected";
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] overflow-hidden" data-testid={`ls-my-ticket-${t.id}`}>
      <button type="button" onClick={() => setExpanded((v) => !v)}
              className="w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-white/[0.04] transition-colors">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ring-1 ring-emerald-400/20"
             style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0.05) 100%)" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6ee7b7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4z" /></svg>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="font-mono font-black text-[10.5px] text-emerald-300 tracking-[0.06em]">#{tno}</span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${sm.bg}`}>
              <span className="h-1 w-1 rounded-full" style={{ background: sm.dot }} />
              {sm.l}
            </span>
          </div>
          <div className="text-[12.5px] font-bold text-white truncate">{t.form_title}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{fmtTime(t.created_at)}</div>
        </div>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={`text-slate-500 mt-1 transition-transform ${expanded ? "rotate-180" : ""}`}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-white/[0.04]">
          {(t.fields || []).filter((f) => !f.url).slice(0, 5).map((f, i) => (
            <div key={i} className="text-[11px] leading-snug flex gap-2">
              <span className="text-slate-500 shrink-0">{f.label}:</span>
              <span className="font-mono font-semibold text-slate-200 break-all">{f.value || "—"}</span>
            </div>
          ))}
          {isFinal && t.resolution_note && (
            <div className="mt-2 p-2.5 rounded-lg bg-amber-500/[0.08] border border-amber-400/20">
              <div className="text-[9px] font-black uppercase tracking-[0.12em] text-amber-300 mb-1">📝 এজেন্টের নোট</div>
              <div className="text-[11.5px] text-amber-100/90 leading-relaxed">{t.resolution_note}</div>
            </div>
          )}
          {isFinal && (
            <div className="text-[10px] text-slate-500 pt-1">
              {t.resolved_by && <>সমাধান করেছেন: <span className="font-semibold text-slate-300">{t.resolved_by}</span> · </>}
              {t.resolved_at && fmtTime(t.resolved_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
